import {
  IOnboardingDrugTestService,
  IOnboardingOption,
  IOnboardingSecondaryScreen,
  IOnboardingServices,
  IOnboardingSpecialistOccasion,
  IOnboardingStep2Context,
  IOnboardingWizardState,
} from "../../../Interfaces/IOnboardingWizardState";

/**
 * Step 2's rules, mirroring `OnboardingStep2Rules` on the server (Feature #5614, remodelled by Epic
 * 6303 Feature CRM2).
 *
 * The server stays authoritative — it re-checks everything on save — but the controls have to know
 * the same rules to be able to *offer* only what is legal, which is what the matrix rulings demand:
 * an impossible combination is prevented by the controls, never reported as a warning afterwards
 * (S2.12, story 5617).
 *
 * The testing matrix is a list of named services (bug 6364): each carries its own test reason code,
 * method, screening device and, on urine, lab panel. The seven-row occasion grid is gone; the server
 * upgrades a payload stored in that shape before it reaches the browser.
 */

export const METHOD_URINE = "Urine";
export const METHOD_ORAL_FLUID = "OralFluid";
export const METHOD_HAIR = "Hair";
export const METHOD_NAIL = "Nail";
/** An alcohol-only testing service: breath alcohol with no drug sample (S2.6). */
export const METHOD_ALCOHOL = "Alcohol";

/** HealthTick service keys (S2.14), as the server's `OnboardingStep2Reference.HealthTickServices`. */
export const HEALTH_TICK_PEM = "PEM";
export const HEALTH_TICK_AHM = "AHM";
export const HEALTH_TICK_FLU = "FLU";
export const HEALTH_TICK_DL9 = "DL9";

/** Other-service keys (S2.18), as the server's `OnboardingStep2Reference.OtherServices`. */
export const OTHER_SERVICE_POLICY_DEVELOPMENT = "PolicyDevelopment";
export const OTHER_SERVICE_TRAINING = "Training";
export const OTHER_SERVICE_OTHER = "Other";

export const COLLECTION_SCREEN = "Screen";
export const COLLECTION_COLLECT_ONLY = "CollectOnly";

export const LAB_OPTION_LAB_IF_NON_NEG = "LabIfNonNeg";
export const LAB_OPTION_NO_LAB_IF_NON_NEG = "NoLabIfNonNeg";
export const LAB_OPTION_ALWAYS_LAB = "AlwaysLab";
export const LAB_OPTION_ASK_RTP = "AskRtp";
export const LAB_OPTION_SCREEN_ONLY = "ScreenOnly";

export const LAB_SERVICE_CONFIRMATION_ONLY = "ConfirmationOnly";
export const LAB_SERVICE_SCREEN_PLUS_CONFIRMATION = "ScreenPlusConfirmation";
export const LAB_SERVICE_SCREEN_ONLY = "ScreenOnly";
export const LAB_SERVICE_NONE = "NoLabService";

export const TEST_TYPE_PRE_DEFINED = "PreDefined";
export const TEST_TYPE_TIME_OF_BOOKING = "TimeOfBooking";

/** The standard AS/NZS 4308 panel — the default lab panel on a urine service (story 6353). */
export const LAB_PANEL_DEFAULT = "PANEL_4308_22";

/** ClientDrugTestService.Name is varchar(200) (story 6349). */
export const SERVICE_NAME_MAX_LENGTH = 200;

/**
 * A collect-only sample always goes to the laboratory, so Always Lab is the only option once the leg
 * is Collect only (Epic 6303 story 6347); Imperans rejects anything else. Mirrors the C# list.
 */
export const COLLECT_ONLY_LAB_OPTIONS = [LAB_OPTION_ALWAYS_LAB];

/** The option lists the service rules read; a subset of `references`. */
export type Step2ServiceReferences = {
  step2TestReasons?: IOnboardingOption[];
  step2ServiceMethods?: IOnboardingOption[];
  step2ScreeningDevices?: IOnboardingOption[];
  step2LabPanels?: IOnboardingOption[];
};

export const isCollectOnly = (collection: string | null | undefined): boolean =>
  collection === COLLECTION_COLLECT_ONLY;

export const isAlcoholOnlyMethod = (method: string | null | undefined): boolean =>
  method === METHOD_ALCOHOL;

export const isScreenedMethod = (method: string | null | undefined): boolean =>
  method === METHOD_URINE || method === METHOD_ORAL_FLUID;

export const isAlcoholOnly = (service: IOnboardingDrugTestService): boolean =>
  isAlcoholOnlyMethod(service.method);

/** The lab service a collection implies (S2.12). */
export const defaultLabService = (
  collection: string | null | undefined,
  labOption: string | null | undefined,
): string => {
  if (isCollectOnly(collection)) return LAB_SERVICE_SCREEN_PLUS_CONFIRMATION;
  // An option that sends nothing to a laboratory implies no laboratory service. Screen Only is a lab
  // SERVICE, not an option (task 5730); it is handled for rows captured while it was offered.
  if (labOption === LAB_OPTION_NO_LAB_IF_NON_NEG || labOption === LAB_OPTION_SCREEN_ONLY)
    return LAB_SERVICE_NONE;
  return LAB_SERVICE_CONFIRMATION_ONLY;
};

/**
 * Collect only fixes the lab service, and so does No Lab If Non-Neg — plus Screen Only, for rows
 * captured while it was wrongly offered as a lab option (S2.12, bug 5700, task 5730).
 */
export const isLabServiceLocked = (
  collection: string | null | undefined,
  labOption: string | null | undefined,
): boolean =>
  isCollectOnly(collection) ||
  labOption === LAB_OPTION_NO_LAB_IF_NON_NEG ||
  labOption === LAB_OPTION_SCREEN_ONLY;

/** Why a control is disabled, so a rule never hides behind a greyed-out field (S2.13). */
export const labServiceLockReason = (
  collection: string | null | undefined,
  labOption: string | null | undefined,
): string | null => {
  if (isCollectOnly(collection))
    return "Collect only sends the sample to the laboratory, so it always screens and confirms.";
  if (labOption === LAB_OPTION_NO_LAB_IF_NON_NEG)
    return "No Lab If Non-Neg keeps the sample on site, so the laboratory does nothing.";
  if (labOption === LAB_OPTION_SCREEN_ONLY)
    return "Screen Only stops at the on-site screen, so there is no laboratory service to choose.";
  return null;
};

export const availableLabOptions = (
  collection: string | null | undefined,
  allKeys: string[],
): string[] => (isCollectOnly(collection) ? COLLECT_ONLY_LAB_OPTIONS : allKeys);

/** The escalation leg always runs on the opposite screened method and is never chosen (S2.5). */
export const oppositeMethod = (method: string | null | undefined): string =>
  method === METHOD_URINE ? METHOD_ORAL_FLUID : METHOD_URINE;

/** Synthetic cannabis is urine-only, and never while the leg is Collect only (S2.6, D13). */
export const isSyntheticAvailable = (
  method: string | null | undefined,
  collection: string | null | undefined,
): boolean => method === METHOD_URINE && !isCollectOnly(collection);

export const syntheticDisabledReason = (
  method: string | null | undefined,
  collection: string | null | undefined,
): string | null => {
  if (method !== METHOD_URINE) return "A synthetic cannabis screen is only possible on a urine sample.";
  if (isCollectOnly(collection))
    return "Collect only sends the sample away unscreened, so there is no on-site synthetic cannabis screen.";
  return null;
};

/** Only a urine service carries a laboratory panel (story 6353). */
export const isLabPanelApplicable = (service: IOnboardingDrugTestService): boolean =>
  service.method === METHOD_URINE;

/** A collect-only urine service must carry one — it is set on every such live service. */
export const isLabPanelRequired = (service: IOnboardingDrugTestService): boolean =>
  isLabPanelApplicable(service) && isCollectOnly(service.collection);

export const screeningDevicesFor = (
  method: string | null | undefined,
  devices: IOnboardingOption[] | undefined,
): IOnboardingOption[] => (devices || []).filter((x) => x.method === method);

/** The nine-panel device is the usual choice on either screened method — the first the list offers. */
export const defaultScreeningDevice = (
  method: string | null | undefined,
  devices: IOnboardingOption[] | undefined,
): string | null => screeningDevicesFor(method, devices).filter(isOffered)[0]?.key ?? null;

/**
 * "<Reason> <Method> + Alcohol" in Imperans spelling ("Pre employment Urine + Alcohol") — the only
 * discriminator Imperans keeps for repeated (reason, method) pairs (story 6349).
 */
export const defaultServiceName = (
  reasonCode: string | null | undefined,
  method: string | null | undefined,
  alcohol: boolean,
  references: Step2ServiceReferences,
): string => {
  const reason = optionLabel(references.step2TestReasons, reasonCode);
  let methodWord = optionLabel(references.step2ServiceMethods, method);
  if (isAlcoholOnlyMethod(method)) methodWord = "Alcohol";
  const name = `${reason === "—" ? "" : reason} ${methodWord === "—" ? "" : methodWord}`.trim();
  return alcohol && !isAlcoholOnlyMethod(method) ? `${name} + Alcohol` : name;
};

/**
 * Keeps the default name current until it has been edited by hand. A name put back to the default is
 * no longer an edited one, so a blank name re-defaulted follows reason and method changes again
 * (TC-6349-06). Mirrors OnboardingStep2Rules.RefreshDefaultName.
 */
export const refreshDefaultName = (
  service: IOnboardingDrugTestService,
  references: Step2ServiceReferences,
): IOnboardingDrugTestService =>
  service.nameEdited && service.name?.trim()
    ? service
    : {
        ...service,
        name: defaultServiceName(service.testReasonCode, service.method, service.alcohol, references),
        nameEdited: false,
      };

/** A new service reads Screen · Lab if Non-Neg · Confirmation Only on the nine-panel device (S2.3, S2.9). */
export const newService = (
  reasonCode: string | null,
  method: string,
  references: Step2ServiceReferences,
): IOnboardingDrugTestService => {
  const alcoholOnly = isAlcoholOnlyMethod(method);
  const service: IOnboardingDrugTestService = {
    id: newId(),
    name: null,
    nameEdited: false,
    testReasonCode: reasonCode,
    method,
    screeningDevice: alcoholOnly ? null : defaultScreeningDevice(method, references.step2ScreeningDevices),
    collection: alcoholOnly ? null : COLLECTION_SCREEN,
    labOption: alcoholOnly ? null : LAB_OPTION_LAB_IF_NON_NEG,
    labService: alcoholOnly ? null : LAB_SERVICE_CONFIRMATION_ONLY,
    labServiceEdited: false,
    labPanel: method === METHOD_URINE ? LAB_PANEL_DEFAULT : null,
    syntheticCannabis: false,
    alcohol: alcoholOnly,
    secondary: null,
  };
  return refreshDefaultName(service, references);
};

/**
 * Re-seeds a service for a collection change: a screen-dependent lab option moves to Always Lab, the
 * lab service returns to the collection's default, the edited marker clears, the synthetic cannabis
 * screen is cleared because Collect only cannot carry one, the screening device is cleared because
 * nothing is screened on site, and a collect-only urine service receives the default lab panel it
 * must carry (S2.12/S2.6, story 6353).
 */
export const applyServiceCollectionChange = (
  service: IOnboardingDrugTestService,
  collection: string,
  references: Step2ServiceReferences,
): IOnboardingDrugTestService => {
  const next: IOnboardingDrugTestService = { ...service, collection };

  if (isCollectOnly(collection)) {
    if (!COLLECT_ONLY_LAB_OPTIONS.includes(next.labOption || "")) next.labOption = LAB_OPTION_ALWAYS_LAB;
    next.syntheticCannabis = false;
    next.screeningDevice = null;
    if (isLabPanelApplicable(next) && !next.labPanel) next.labPanel = LAB_PANEL_DEFAULT;
  } else if (!next.screeningDevice) {
    next.screeningDevice = defaultScreeningDevice(next.method, references.step2ScreeningDevices);
  }

  next.labService = defaultLabService(next.collection, next.labOption);
  next.labServiceEdited = false;
  return next;
};

/** A lab-option change re-seeds the lab service unless it was deliberately overridden (S2.4). */
export const applyServiceLabOptionChange = (
  service: IOnboardingDrugTestService,
  labOption: string,
): IOnboardingDrugTestService => {
  const next: IOnboardingDrugTestService = { ...service, labOption };
  if (!next.labServiceEdited || isLabServiceLocked(next.collection, labOption))
    next.labService = defaultLabService(next.collection, labOption);
  return next;
};

/**
 * Moves a service onto another method. An alcohol-only service carries no sample, so every laboratory
 * field is cleared; a move back onto a screened method seeds the defaults again. The name follows the
 * change until somebody has edited it (story 6349).
 */
export const applyServiceMethodChange = (
  service: IOnboardingDrugTestService,
  method: string,
  references: Step2ServiceReferences,
): IOnboardingDrugTestService => {
  const next: IOnboardingDrugTestService = { ...service, method };

  if (isAlcoholOnlyMethod(method)) {
    next.collection = null;
    next.labOption = null;
    next.labService = null;
    next.labServiceEdited = false;
    next.labPanel = null;
    next.screeningDevice = null;
    next.syntheticCannabis = false;
    next.secondary = null;
    next.alcohol = true;
  } else {
    // Leaving alcohol only, the tick it forced on returns to a new service's default, as every
    // other field it cleared does (TC-6368-03); a move between sample methods keeps the choice.
    if (isAlcoholOnlyMethod(service.method)) next.alcohol = false;
    next.collection = next.collection ?? COLLECTION_SCREEN;
    next.labOption = next.labOption ?? LAB_OPTION_LAB_IF_NON_NEG;
    next.labService = next.labService ?? defaultLabService(next.collection, next.labOption);
    const devices = screeningDevicesFor(method, references.step2ScreeningDevices);
    if (isCollectOnly(next.collection)) next.screeningDevice = null;
    else if (!devices.some((x) => x.key === next.screeningDevice))
      next.screeningDevice = defaultScreeningDevice(method, references.step2ScreeningDevices);
    if (method !== METHOD_URINE) {
      next.syntheticCannabis = false;
      next.labPanel = null;
    } else if (!next.labPanel) {
      next.labPanel = LAB_PANEL_DEFAULT;
    }
  }

  return refreshDefaultName(next, references);
};

export const applyServiceReasonChange = (
  service: IOnboardingDrugTestService,
  reasonCode: string,
  references: Step2ServiceReferences,
): IOnboardingDrugTestService =>
  refreshDefaultName({ ...service, testReasonCode: reasonCode }, references);

export const applyServiceAlcoholChange = (
  service: IOnboardingDrugTestService,
  alcohol: boolean,
  references: Step2ServiceReferences,
): IOnboardingDrugTestService => refreshDefaultName({ ...service, alcohol }, references);

/** A copied service is independent of its source: a new id and a name that reads as a copy (S2.8). */
export const copyService = (service: IOnboardingDrugTestService): IOnboardingDrugTestService => ({
  ...service,
  id: newId(),
  name: `${service.name || ""} (copy)`.trim(),
  nameEdited: true,
  secondary: service.secondary ? { ...service.secondary } : null,
});

/** A new escalation leg reads Collect only · Screen + Confirmation (S2.5). */
export const newSecondary = (): IOnboardingSecondaryScreen => ({
  collection: COLLECTION_COLLECT_ONLY,
  labService: LAB_SERVICE_SCREEN_PLUS_CONFIRMATION,
  syntheticCannabis: false,
});

/**
 * The escalation leg's lab service always re-seeds from its collection and the edited marker clears —
 * unlike the primary, there is no long-lived override here to protect (S2.5).
 */
export const applySecondaryCollectionChange = (
  secondary: IOnboardingSecondaryScreen,
  collection: string,
): IOnboardingSecondaryScreen => ({
  ...secondary,
  collection,
  labService: defaultLabService(collection, null),
  syntheticCannabis: isCollectOnly(collection) ? false : secondary.syntheticCannabis,
});

/** How a service is named in a message: its name, or its reason and method while it has none. */
export const serviceLabel = (
  service: IOnboardingDrugTestService,
  references: Step2ServiceReferences,
): string =>
  service.name?.trim() ||
  `${optionLabel(references.step2TestReasons, service.testReasonCode)} ${optionLabel(
    references.step2ServiceMethods,
    service.method,
  )}`.trim();

/** Whether a service on this method counts: its collection method is ticked, or it is alcohol only. */
export const isMethodEnabled = (
  services: IOnboardingServices,
  method: string | null | undefined,
): boolean => {
  if (method === METHOD_URINE) return services.urineEnabled;
  if (method === METHOD_ORAL_FLUID) return services.oralFluidEnabled;
  if (method === METHOD_HAIR) return services.hairEnabled;
  if (method === METHOD_NAIL) return services.nailEnabled;
  return isAlcoholOnlyMethod(method);
};

/**
 * The services that count towards completion and are sent to Imperans: those whose method is
 * enabled. A row kept behind an unticked method is neither validated nor sent (bug 6371).
 */
export const activeServices = (services: IOnboardingServices): IOnboardingDrugTestService[] =>
  (services.drugTestServices || []).filter((x) => isMethodEnabled(services, x.method));

/** The first name used by two active services — in its first spelling, as the server reports it — or null (story 6349). */
export const duplicateServiceName = (services: IOnboardingServices): string | null => {
  const seen = new Map<string, string>();
  for (const service of activeServices(services)) {
    const trimmed = (service.name || "").trim();
    const key = trimmed.toLowerCase();
    if (!key) continue;
    const first = seen.get(key);
    if (first !== undefined) return first;
    seen.set(key, trimmed);
  }
  return null;
};

/**
 * One service's own conditions, in the order the row reads left to right — the same order and
 * wording as the server's `ValidateService`.
 */
export const validateService = (
  service: IOnboardingDrugTestService,
  references: Step2ServiceReferences,
): string | null => {
  const where = serviceLabel(service, references);

  if (!(references.step2TestReasons || []).some((x) => x.key === service.testReasonCode))
    return `Choose a test reason for ${where}.`;
  if (!(references.step2ServiceMethods || []).some((x) => x.key === service.method))
    return `Choose a method for ${where}.`;
  if (!service.name?.trim()) return `Enter a service name for ${where}.`;
  if (service.name.trim().length > SERVICE_NAME_MAX_LENGTH)
    return `Shorten the service name for ${where} to ${SERVICE_NAME_MAX_LENGTH} characters or fewer.`;

  if (isAlcoholOnly(service)) return null;

  if (service.collection !== COLLECTION_SCREEN && service.collection !== COLLECTION_COLLECT_ONLY)
    return `Choose a collection for ${where}.`;
  const offeredOptions = [LAB_OPTION_LAB_IF_NON_NEG, LAB_OPTION_NO_LAB_IF_NON_NEG, LAB_OPTION_ALWAYS_LAB, LAB_OPTION_ASK_RTP];
  if (!service.labOption || !availableLabOptions(service.collection, offeredOptions).includes(service.labOption))
    return `Choose a lab option for ${where}.`;
  if (![LAB_SERVICE_CONFIRMATION_ONLY, LAB_SERVICE_SCREEN_PLUS_CONFIRMATION, LAB_SERVICE_SCREEN_ONLY, LAB_SERVICE_NONE].includes(service.labService || ""))
    return `Choose a lab service for ${where}.`;

  if (isCollectOnly(service.collection)) {
    if (service.screeningDevice)
      return `A collect-only service is not screened on site, so ${where} cannot carry a screening device.`;
  } else if (!screeningDevicesFor(service.method, references.step2ScreeningDevices).some((x) => x.key === service.screeningDevice)) {
    return `Choose a screening device for ${where}.`;
  }

  if (isLabPanelRequired(service) && !service.labPanel)
    return `Choose a lab panel for ${where} — a collect-only urine service must carry one.`;
  if (service.labPanel) {
    if (!isLabPanelApplicable(service)) return `Only a urine service carries a lab panel — clear it on ${where}.`;
    if (!(references.step2LabPanels || []).some((x) => x.key === service.labPanel))
      return `Choose a lab panel offered for ${where}.`;
  }

  if (service.syntheticCannabis && !isSyntheticAvailable(service.method, service.collection))
    return `Synthetic Cannabis is only available on a screened urine service — clear it on ${where}.`;

  if (service.secondary) {
    if (service.secondary.collection !== COLLECTION_SCREEN && service.secondary.collection !== COLLECTION_COLLECT_ONLY)
      return `Choose a collection for the secondary screen on ${where}.`;
    if (!service.secondary.labService) return `Choose a lab service for the secondary screen on ${where}.`;
    if (service.secondary.syntheticCannabis && !isSyntheticAvailable(oppositeMethod(service.method), service.secondary.collection))
      return `Synthetic Cannabis is only available on a screened urine leg — clear it on the secondary screen of ${where}.`;
  }

  return null;
};

/**
 * Whether a hair or nail occasion is currently below its floor of one service line (S2.7, bug 5712).
 * A stored card with no test type reads as Pre Defined, matching what the radio shows for it.
 */
export const needsServiceLineFloor = (card: {
  included?: boolean;
  testType?: string | null;
  serviceLines?: unknown[] | null;
}): boolean => {
  if (!card?.included) return false;
  if ((card.testType || TEST_TYPE_PRE_DEFINED) === TEST_TYPE_TIME_OF_BOOKING) return false;
  return !(card.serviceLines?.length);
};

export const labsForMethod = (
  method: string,
  context: IOnboardingStep2Context,
): string[] => (method === METHOD_URINE ? context.urineLabs : context.oralFluidLabs);

export const isLabLocked = (method: string, context: IOnboardingStep2Context): boolean =>
  method === METHOD_ORAL_FLUID && context.oralFluidLabLocked;

/**
 * Whether the client picks its own confirmation laboratory, mirroring the server rule of the same
 * name. New Zealand does — Hill Laboratory or CHL, per method. Australia does not: the laboratory
 * follows each office's state (decision 4 as ruled 9 Sep 2026, task 6008).
 */
export const isLabChosenByClient = (context: IOnboardingStep2Context): boolean =>
  !context.isAustralia;

/**
 * The laboratory one Australian office confirms at, from its state: Queensland at SNP, New South
 * Wales at DHM, every other state at SafeWork. Null for a code this does not recognise.
 */
export const labForAustralianState = (stateCode: string | null | undefined): string | null => {
  const code = `${stateCode ?? ""}`.trim().toUpperCase();
  if (code === "QLD") return "SNP";
  if (code === "NSW") return "DHM";
  return ["VIC", "WA", "SA", "TAS", "NT", "ACT"].includes(code) ? "SafeWork" : null;
};

/** The laboratories this client's offices imply, one entry per state in play (task 6008). */
export const derivedSiteLabs = (
  context: IOnboardingStep2Context,
): { state: string; lab: string }[] =>
  (context.siteStates || [])
    .map((state) => ({ state, lab: labForAustralianState(state) }))
    .filter((x): x is { state: string; lab: string } => Boolean(x.lab));

/** The confirmation lab a method should start on, as resolved by the server for this country. */
export const defaultLabForMethod = (
  method: string,
  context: IOnboardingStep2Context,
): string | null =>
  method === METHOD_URINE ? context.defaultUrineLab : context.defaultOralFluidLab;

/**
 * The tests a set of levels implies, de-duplicated and cumulative: Standard implies Basic, Premium
 * implies both, Custom contributes nothing (S2.14).
 */
export const expandLevels = (
  levels: string[] | null | undefined,
  levelTests: Record<string, string[]>,
): string[] => {
  const effective = new Set<string>();
  (levels || []).forEach((level) => {
    if (level === "Premium") ["Basic", "Standard", "Premium"].forEach((x) => effective.add(x));
    else if (level === "Standard") ["Basic", "Standard"].forEach((x) => effective.add(x));
    else effective.add(level);
  });

  const tests = new Set<string>();
  effective.forEach((level) => (levelTests[level] || []).forEach((test) => tests.add(test)));
  return Array.from(tests);
};

/** Alcohol is in play from any active service carrying it, or any alcohol-only service (S2.10). */
export const isAlcoholInPlay = (services: IOnboardingServices | null): boolean => {
  if (!services) return false;
  return activeServices(services).some((x) => x.alcohol || isAlcoholOnly(x));
};

/** A fresh step-2 payload: TDDA, no services yet, one hair and nail card per hair test reason (S2.1/S2.7). */
export const emptyServices = (
  hairReasonCodes: string[],
  context: IOnboardingStep2Context,
): IOnboardingServices => ({
  serviceLine: "TDDA",
  urineEnabled: false,
  oralFluidEnabled: false,
  hairEnabled: false,
  nailEnabled: false,
  urineLab: context.defaultUrineLab,
  oralFluidLab: context.defaultOralFluidLab,
  drugTestServices: [],
  alcoholBandUnder20: null,
  alcoholBand20Plus: null,
  alcoholUnit: context.alcoholUnit,
  hairOccasions: hairReasonCodes.map(newSpecialist),
  nailOccasions: hairReasonCodes.map(newSpecialist),
  healthTick: null,
  otherServices: null,
  serviceStartDates: {},
  isUrgentSetup: false,
});

export const newSpecialist = (occasionCode: string): IOnboardingSpecialistOccasion => ({
  occasionCode,
  included: false,
  testType: TEST_TYPE_PRE_DEFINED,
  testReason: null,
  serviceLines: [],
});

/**
 * Whether any card here needs its Test Reason prefilled (S2.7, bug 5857): included, Pre Defined, and
 * still empty.
 */
export const specialistCardsNeedTestReason = (cards: IOnboardingSpecialistOccasion[]): boolean =>
  cards.some(
    (card) =>
      card.included && card.testType !== TEST_TYPE_TIME_OF_BOOKING && !card.testReason?.trim(),
  );

/** Fills the gap `specialistCardsNeedTestReason` finds, leaving every other card untouched. */
export const withPrefilledTestReasons = (
  cards: IOnboardingSpecialistOccasion[],
  occasions: { key: string; label: string }[],
): IOnboardingSpecialistOccasion[] =>
  cards.map((card) =>
    card.included && card.testType !== TEST_TYPE_TIME_OF_BOOKING && !card.testReason?.trim()
      ? { ...card, testReason: optionLabel(occasions, card.occasionCode) }
      : card,
  );

/**
 * The hair and nail card lists are padded to the hair test reasons, so an older payload still renders
 * (S2.7, story 6359). A stored card that is included on a reason the list does not offer is kept at
 * the end so the choice stays visible and the validation can name it; every other collection gets a
 * default so nothing reads as undefined.
 */
export const withAllOccasions = (
  services: IOnboardingServices,
  hairReasonCodes: string[],
): IOnboardingServices => ({
  ...services,
  drugTestServices: services.drugTestServices || [],
  hairOccasions: padCards(services.hairOccasions, hairReasonCodes),
  nailOccasions: padCards(services.nailOccasions, hairReasonCodes),
  serviceStartDates: services.serviceStartDates || {},
});

const padCards = (cards: IOnboardingSpecialistOccasion[] | null, codes: string[]) => {
  const padded = codes.map(
    (code) => (cards || []).find((card) => card.occasionCode === code) || newSpecialist(code),
  );
  const extras = (cards || []).filter((card) => card.included && !codes.includes(card.occasionCode));
  return [...padded, ...extras];
};

export const includesTdda = (serviceLine: string | null | undefined): boolean =>
  !serviceLine || serviceLine === "TDDA" || serviceLine === "Both";

export const includesHealthTick = (serviceLine: string | null | undefined): boolean =>
  serviceLine === "HealthTick" || serviceLine === "Both";

/**
 * The services that carry a start date field right now (S2.19), mirroring the server's
 * `IsStartDateLive`: a collection method's while TDDA is on the service line and the method is
 * ticked, a HealthTick service's while HealthTick is on the line and the service is selected, and
 * an other service's while it is ticked. A date behind a hidden field is kept but never sent, so it
 * must not be offered either — it could not be cleared (TC-6351-04).
 */
export const startDateServices = (
  services: IOnboardingServices,
): { key: string; label: string }[] => {
  const selected: { key: string; label: string }[] = [];
  if (includesTdda(services.serviceLine)) {
    if (services.urineEnabled) selected.push({ key: METHOD_URINE, label: "Urine" });
    if (services.oralFluidEnabled) selected.push({ key: METHOD_ORAL_FLUID, label: "Oral fluid" });
    if (services.hairEnabled) selected.push({ key: METHOD_HAIR, label: "Hair" });
    if (services.nailEnabled) selected.push({ key: METHOD_NAIL, label: "Nail" });
  }
  if (services.otherServices?.policyDevelopment)
    selected.push({ key: OTHER_SERVICE_POLICY_DEVELOPMENT, label: "Policy Development" });
  if (services.otherServices?.training)
    selected.push({ key: OTHER_SERVICE_TRAINING, label: "Training" });
  if (services.otherServices?.other) selected.push({ key: OTHER_SERVICE_OTHER, label: "Other service" });
  const healthTick = includesHealthTick(services.serviceLine) ? services.healthTick : null;
  if (healthTick?.pemSelected) selected.push({ key: HEALTH_TICK_PEM, label: "PEM" });
  if (healthTick?.ahmSelected) selected.push({ key: HEALTH_TICK_AHM, label: "AHM" });
  if (healthTick?.fluSelected) selected.push({ key: HEALTH_TICK_FLU, label: "Flu vouchers" });
  if (healthTick?.dl9Selected) selected.push({ key: HEALTH_TICK_DL9, label: "DL9s" });
  return selected;
};

/** A new internal id, so a pasted service, service line or role is independent of its source (S2.8). */
export const newId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const optionLabel = (
  options: { key: string; label: string }[] | undefined,
  key: string | null | undefined,
): string => options?.find((x) => x.key === key)?.label ?? (key || "—");

export const optionDescription = (
  options: { key: string; description?: string }[] | undefined,
  key: string | null | undefined,
): string => options?.find((x) => x.key === key)?.description ?? "";

/**
 * A hair or nail panel's label, carrying its method exactly once (S2.7, story 6359).
 *
 * The panel lists are Imperans's own names, and most of them already lead with the method ("Hair
 * 10-Panel", "Nail 10 Panel") — prefixing those again read "Nail Nail 10 Panel". Only a name that
 * does not ("ETG Only", "MOCCA B + ETG") gains the prefix, so the open list and the closed field
 * both name the method once.
 */
export const specialistPanelLabel = (
  panels: { key: string; label: string }[] | undefined,
  key: string | null | undefined,
  method: string,
): string => {
  const label = optionLabel(panels, key);
  return label.toLowerCase().startsWith(`${method.toLowerCase()} `) ? label : `${method} ${label}`;
};

/** The drug test reason codes step 2 offers (bug 6365). */
export const testReasonCodesFrom = (state: IOnboardingWizardState): string[] =>
  (state.references.step2TestReasons || []).map((x) => x.key);

/** The hair test reason codes the hair and nail cards are keyed on (story 6359). */
export const hairReasonCodesFrom = (state: IOnboardingWizardState): string[] =>
  (state.references.step2HairTestReasons || []).map((x) => x.key);

// ── HT Pre-employment: Copy from TDDA (S2.16, story 5626) ─────────────────────────────────────

/**
 * The TDDA pre-employment service the HT pre-employment block copies from, urine preferred where both
 * methods have one (S2.16). `bothMethodsHaveOne` is carried because that is the case the copy is
 * easiest to misread (bug 5719).
 */
export type TddaPreEmploymentSource = {
  method: string;
  row: IOnboardingDrugTestService;
  lab: string | null;
  bothMethodsHaveOne: boolean;
};

export const PRE_EMPLOYMENT_REASON = "PRE_EMPLOYMENT";

export const tddaPreEmploymentSource = (
  services: IOnboardingServices | null | undefined,
): TddaPreEmploymentSource | null => {
  if (!services) return null;
  const active = activeServices(services).filter(
    (x) => x.testReasonCode === PRE_EMPLOYMENT_REASON && !isAlcoholOnly(x),
  );
  const urine = active.find((x) => x.method === METHOD_URINE);
  const oral = active.find((x) => x.method === METHOD_ORAL_FLUID);
  const bothMethodsHaveOne = !!urine && !!oral;

  if (urine) return { method: METHOD_URINE, row: urine, lab: services.urineLab ?? null, bothMethodsHaveOne };
  if (oral) return { method: METHOD_ORAL_FLUID, row: oral, lab: services.oralFluidLab ?? null, bothMethodsHaveOne };
  return null;
};

/** What the copy actually did, in the same words the controls use (S2.16, bug 5719). */
export const describeCopiedPreEmployment = (
  source: TddaPreEmploymentSource,
  references: {
    step2Collections?: { key: string; label: string }[];
    step2LabOptions?: { key: string; label: string }[];
    step2LabServices?: { key: string; label: string }[];
    step2Laboratories?: { key: string; label: string }[];
  },
): string => {
  const methodName = source.method === METHOD_URINE ? "urine" : "oral fluid";
  const from = source.bothMethodsHaveOne
    ? `Copied from the TDDA ${methodName} pre-employment service — both methods have one, so urine takes precedence.`
    : `Copied from the TDDA ${methodName} pre-employment service.`;

  const values = [
    `collection method ${source.method === METHOD_URINE ? "Urine" : "Oral fluid"}`,
    `collection ${optionLabel(references?.step2Collections, source.row.collection)}`,
    `lab option ${optionLabel(references?.step2LabOptions, source.row.labOption)}`,
    `lab service ${optionLabel(references?.step2LabServices, source.row.labService)}`,
    `confirmation laboratory ${
      source.lab
        ? optionLabel(references?.step2Laboratories, source.lab)
        : "not set on the TDDA service"
    }`,
  ];

  return `${from} Now set to: ${values.join(", ")}.`;
};

/**
 * What the *closed* control shows once its list carries the option wordings too (S2.13, task 5729):
 * the label alone, never the two-line block, and nothing at all for an unset value.
 */
export const renderOptionValue =
  (
    options: { key: string; label: string }[] | undefined,
    labelFor?: (key: string) => string,
  ) =>
  (value: unknown): string => {
    const key = typeof value === "string" ? value : "";
    if (!key) return "";
    return labelFor ? labelFor(key) : optionLabel(options, key);
  };

/**
 * Whether an option belongs in a dropdown (task 5730). Absent means offered, so every other option
 * set is untouched.
 */
export const isOffered = (option: { offered?: boolean } | null | undefined): boolean =>
  option?.offered !== false;
